import React, {useMemo, useState} from "react";
import QTextBox from "../../../common/components/QTextBox";
import {useFormik} from "formik";
import * as Yup from "yup";
import QLoading from "../../../common/components/QLoading";
import QButton from "../../../common/components/QButton";
import {ResponseInterface} from "../../../interfaces/ResponseInterface";
import {config} from "../../../config";
import {useHistory, useLocation} from "react-router-dom";
import {RouteDefine} from "../../../routes/RouteDefine";
import {useDispatch} from "react-redux";
import UserApi from "../../../common/api/functions/User";
import {setToast} from "../../../app/rootReducer";
import {QHelpers} from "../../../common/helpers";
import {GeneralConstant} from "../../../common/constant/GeneralConstant";
import {useTranslation} from "react-i18next";
import {resolveOnboardingPath} from "../../../common/helpers/onboardingResume";
import {
    clearEconomicRegistrationSession,
    getEconomicRegistrationSession,
    saveEconomicRegistrationSession,
} from "../../../common/helpers/economicRegistrationSession";

interface LocationState {
    token?: string;
    agreementNumber?: string | number;
    company?: {
        name?: string;
    };
}

/**
 * Marketplace signup: user chooses login email/password (not prefilled from e-conomic).
 */
export function RegisterFromEconomic() {
    const {t} = useTranslation();
    const history = useHistory();
    const location = useLocation<LocationState | undefined>();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);

    const session = useMemo(() => {
        const fromState = location.state;
        const fromQuery = QHelpers.object.convertParamUrlToObject(window.location.search);
        const fromStorage = getEconomicRegistrationSession();

        const token = fromState?.token || fromQuery?.token || fromStorage?.token;
        const agreementNumber = fromState?.agreementNumber ?? fromStorage?.agreementNumber;
        const company = fromState?.company || fromStorage?.company;

        if (token && agreementNumber != null && agreementNumber !== "") {
            saveEconomicRegistrationSession({
                token,
                agreementNumber,
                company,
            });
        }

        return {
            token,
            agreementNumber,
            companyName: company?.name,
        };
    }, [location.state]);

    const {token, agreementNumber, companyName} = session;

    const formik = useFormik({
        initialValues: {
            email: "",
            password: "",
            password_confirm: "",
        },
        validationSchema: Yup.object({
            email: Yup.string().email().required(t("auth.login.enter_email")),
            password: Yup.string().required(t("auth.login.enter_password")).min(6),
            password_confirm: Yup.string()
                .oneOf([Yup.ref("password"), null], t("auth.register.repeat_not_match"))
                .required(t("auth.register.repeat_password")),
        }),
        onSubmit: async (values, {setFieldError}) => {
            if (!token || agreementNumber == null || agreementNumber === "") {
                dispatch(setToast({
                    message: t("auth.register.missing_economic_session"),
                    type: config.TOAST_TYPE.ERROR,
                }));
                history.push(RouteDefine.login);
                return;
            }

            setLoading(true);
            try {
                const response: ResponseInterface | any = await UserApi.registerFromEconomic({
                    token,
                    accountNumber: agreementNumber,
                    email: values.email,
                    password: values.password,
                    password_confirm: values.password_confirm,
                });

                if (response?.status === config.STATUS_CODE.OK) {
                    const data = response.data.data;
                    clearEconomicRegistrationSession();
                    await localStorage.setItem(
                        config.LOCAL_STORE.ACCOUNT,
                        JSON.stringify(data),
                    );
                    await localStorage.setItem(
                        config.LOCAL_STORE.ROLE,
                        QHelpers.string.encrypt(GeneralConstant.user.role.user),
                    );

                    dispatch(setToast({
                        message: t("auth.register.create_success"),
                        type: config.TOAST_TYPE.SUCCESS,
                    }));

                    const next = resolveOnboardingPath(data);
                    history.push(next, {
                        agreementNumber: data.agreementNumber,
                        user: data.economic_user || {name: data.user?.name},
                        company: data.company,
                    });
                    return;
                }

                dispatch(setToast({
                    message: response?.data?.message || t("auth.register.create_fail"),
                    type: config.TOAST_TYPE.ERROR,
                }));
            } catch (error: any) {
                const message = error?.data?.message;
                if (message && typeof message === "object") {
                    Object.keys(message).forEach((key) => {
                        setFieldError(key, Array.isArray(message[key]) ? message[key][0] : String(message[key]));
                    });
                } else {
                    dispatch(setToast({
                        message: (typeof message === "string" ? message : null) || t("auth.register.create_fail"),
                        type: config.TOAST_TYPE.ERROR,
                    }));
                }
            } finally {
                setLoading(false);
            }
        },
    });

    if (!token || agreementNumber == null || agreementNumber === "") {
        return (
            <div className="mt-4">
                <p className="text-muted">{t("auth.register.missing_economic_session")}</p>
                <QButton
                    type="primary"
                    content={t("auth.register.login")}
                    onClick={() => history.push(RouteDefine.login)}
                />
            </div>
        );
    }

    return (
        <div className="mt-4 position-relative">
            <QLoading loading={loading}/>
            <h2 className="mb-2 mt-3 fs-32 cl-orange-zp fw-400">{t("auth.register.create_your_account")}</h2>
            <p className="text-muted mb-4">
                {t("auth.register.economic_context", {
                    company: companyName || agreementNumber,
                    agreement: agreementNumber,
                })}
            </p>
            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    formik.handleSubmit();
                }}
            >
                <QTextBox
                    label={t("auth.register.email")}
                    value={formik.values.email}
                    onChange={(value: string) => formik.setFieldValue("email", value)}
                    error={formik.touched.email && formik.errors.email ? String(formik.errors.email) : undefined}
                />
                <QTextBox
                    label={t("auth.register.password")}
                    type="password"
                    value={formik.values.password}
                    onChange={(value: string) => formik.setFieldValue("password", value)}
                    error={formik.touched.password && formik.errors.password ? String(formik.errors.password) : undefined}
                />
                <QTextBox
                    label={t("auth.register.repeat_password")}
                    type="password"
                    value={formik.values.password_confirm}
                    onChange={(value: string) => formik.setFieldValue("password_confirm", value)}
                    error={formik.touched.password_confirm && formik.errors.password_confirm
                        ? String(formik.errors.password_confirm)
                        : undefined}
                />
                <div className="mt-4">
                    <QButton
                        type="primary"
                        className="w-100"
                        content={t("auth.register.create_account")}
                        onClick={() => formik.handleSubmit()}
                    />
                </div>
                <p className="mt-3 mb-0">
                    <a
                        className="cl-orange-zp fw-bold"
                        href="#"
                        onClick={(event) => {
                            event.preventDefault();
                            history.push(RouteDefine.login);
                        }}
                    >
                        {t("auth.register.login")}
                    </a>
                </p>
            </form>
        </div>
    );
}

export default RegisterFromEconomic;
